import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ISearchDictResultItem } from '@models/search/ISearchDictResultItem';
import { ISearchDocResultItem } from '@models/search/ISearchDocResultItem';
import {
    ISearchExtraBaseFilter,
    ISearchExtraFlowFieldWithValue,
    ISearchExtraFlowSetting,
} from '@models/search/ISearchExtraFilters';
import { ISearchReportResultItem } from '@models/search/ISearchReportResultItem';
import { SearchScope, SearchStatus } from '@models/search/search.types';
import { IFieldElem } from '@models/IFormData';
import { IForms } from '@models/Forms/IForms';
import { SLICE_NAMES } from '@/constants';

export const DEFAULT_PAGE_SIZE: number = 10;
export const PAGE_SIZES: readonly number[] = [10, 20, 50] as const;

export type SearchResultItem = ISearchDocResultItem | ISearchDictResultItem | ISearchReportResultItem;

/**
 * Состояние одной области. `queryKey` — ключ показанного набора, `requestId` — id
 * запроса, который сейчас в полёте: ответ пишется в стор только если его id всё ещё
 * актуален, поэтому обогнавший себя ответ не перетирает более свежий.
 */
export interface IScopeSearchState<TItem = SearchResultItem> {
    items: TItem[];
    /** Ключ набора без страницы — к нему относится `totalCount`. */
    resultSetKey: string;
    totalCount: number | undefined;
    queryKey: string;
    pendingQueryKey: string;
    requestId: number;
    status: SearchStatus;
    error?: string;
}

export interface ISearchStarted {
    scope: SearchScope;
    queryKey: string;
    requestId: number;
}

export interface ISearchSucceeded {
    scope: SearchScope;
    requestId: number;
    queryKey: string;
    resultSetKey: string;
    items: SearchResultItem[];
    totalCount: number | undefined;
    /** Ответ на запрос с `requireTotalCount` — только он вправе изменить счётчик. */
    requireTotalCount: boolean;
}

export interface ISearchFailed {
    scope: SearchScope;
    requestId: number;
    error: string;
}

export interface ISearchsState {
    docs: IScopeSearchState<ISearchDocResultItem>;
    dicts: IScopeSearchState<ISearchDictResultItem>;
    reports: IScopeSearchState<ISearchReportResultItem>;
    /** Метаданные фильтра документов: список потоков, загружается один раз. */
    filterMeta: {
        flows?: ISearchExtraFlowSetting[];
        isLoading: boolean;
    };
    /** Черновик фильтра: то, что пользователь редактирует в панели. */
    filterDraft: {
        selectedFlow?: ISearchExtraFlowSetting;
        fieldsValues?: ISearchExtraFlowFieldWithValue[];
    };
    /** Применённый фильтр: то, по чему показаны результаты. */
    appliedFilter?: ISearchExtraBaseFilter;
    baseRequisitesFields?: Record<string, IFieldElem>;
    baseRequisitesForms?: IForms;
}

const emptyScopeState = <TItem>(): IScopeSearchState<TItem> => ({
    items: [] as TItem[],
    resultSetKey: '',
    totalCount: undefined,
    queryKey: '',
    pendingQueryKey: '',
    requestId: 0,
    status: 'idle',
});

const initialState: ISearchsState = {
    docs: emptyScopeState<ISearchDocResultItem>(),
    dicts: emptyScopeState<ISearchDictResultItem>(),
    reports: emptyScopeState<ISearchReportResultItem>(),
    filterMeta: { isLoading: false },
    filterDraft: {},
};

/** Immer-совместимый доступ к области без ветвлений на каждом редьюсере. */
const scopeOf = (state: ISearchsState, scope: SearchScope): IScopeSearchState<SearchResultItem> => state[scope];

export const SearchSlice = createSlice({
    name: SLICE_NAMES.SEARCH,
    initialState,
    reducers: {
        searchStarted: (state, action: PayloadAction<ISearchStarted>) => {
            const scopeState = scopeOf(state, action.payload.scope);
            scopeState.requestId = action.payload.requestId;
            scopeState.pendingQueryKey = action.payload.queryKey;
            scopeState.error = undefined;
            // Данные уже на экране — не гасим их: это уточнение, а не первая загрузка.
            scopeState.status = scopeState.items.length > 0 ? 'refining' : 'loading';
        },

        searchSucceeded: (state, action: PayloadAction<ISearchSucceeded>) => {
            const scopeState = scopeOf(state, action.payload.scope);
            if (scopeState.requestId !== action.payload.requestId) {
                return;
            }

            scopeState.items = action.payload.items;
            scopeState.queryKey = action.payload.queryKey;
            scopeState.pendingQueryKey = '';
            scopeState.status = 'success';
            scopeState.error = undefined;

            if (action.payload.requireTotalCount) {
                // `0` — валидный ответ: раньше falsy-проверка оставляла счётчик от прошлого запроса.
                scopeState.totalCount = action.payload.totalCount ?? 0;
                scopeState.resultSetKey = action.payload.resultSetKey;
            } else if (scopeState.resultSetKey !== action.payload.resultSetKey) {
                // Счётчик относился к другому набору — он больше не действителен.
                scopeState.totalCount = undefined;
                scopeState.resultSetKey = action.payload.resultSetKey;
            }
        },

        searchFailed: (state, action: PayloadAction<ISearchFailed>) => {
            const scopeState = scopeOf(state, action.payload.scope);
            if (scopeState.requestId !== action.payload.requestId) {
                return;
            }

            scopeState.status = 'error';
            scopeState.error = action.payload.error;
            scopeState.pendingQueryKey = '';
        },

        searchCancelled: (state, action: PayloadAction<{ scope: SearchScope; requestId: number }>) => {
            const scopeState = scopeOf(state, action.payload.scope);
            if (scopeState.requestId !== action.payload.requestId) {
                return;
            }

            // Прерванный поиск сам не возобновится — состояние должно это показывать.
            scopeState.status = scopeState.items.length > 0 ? 'success' : 'cancelled';
            scopeState.pendingQueryKey = '';
        },

        setBaseRequisitesFields: (
            state,
            action: PayloadAction<{ fields: Record<string, IFieldElem>; forms: IForms }>,
        ) => {
            state.baseRequisitesFields = action.payload.fields;
            state.baseRequisitesForms = action.payload.forms;
        },

        setExtraFlowsFilterLoading: (state, action: PayloadAction<boolean>) => {
            state.filterMeta.isLoading = action.payload;
        },

        setExtraFlowsFilterData: (state, action: PayloadAction<ISearchExtraFlowSetting[]>) => {
            state.filterMeta.flows = action.payload;
        },

        selectExtraFilterFlow: (state, action: PayloadAction<ISearchExtraFlowSetting | undefined>) => {
            state.filterDraft.selectedFlow = action.payload;
            state.filterDraft.fieldsValues = action.payload?.fields.map(
                (field) => ({ ...field, value: undefined }) as ISearchExtraFlowFieldWithValue,
            );
        },

        /** Применение фильтра: черновик становится показанным набором условий. */
        applyDocumentFilter: (state, action: PayloadAction<ISearchExtraBaseFilter>) => {
            state.appliedFilter = action.payload;
            state.filterDraft = {
                selectedFlow: action.payload.flowsFields?.selectedFlow,
                fieldsValues: action.payload.flowsFields?.fieldsValues,
            };
        },

        /** «Очистить»: снимает и черновик, и применённые условия. */
        clearDocumentFilter: (state) => {
            state.appliedFilter = undefined;
            state.filterDraft = {};
        },
    },
});

export const { searchStarted, searchSucceeded, searchFailed, searchCancelled, setBaseRequisitesFields } =
    SearchSlice.actions;
export default SearchSlice.reducer;
